import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as clientsApi from '../api/clients';
import * as quotesApi from '../api/quotes';
import * as invoicesApi from '../api/invoices';
import * as opportunitiesApi from '../api/opportunities';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';

// Utility functions matching existing styling
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    // Handle both old format (with timezone) and new format (DATE only)
    if (dateString.includes('T') && dateString.includes('Z')) {
      const dateOnly = dateString.split('T')[0];
      return new Date(dateOnly + 'T00:00:00').toLocaleDateString();
    } else {
      return new Date(dateString + 'T00:00:00').toLocaleDateString();
    }
  } catch (error) {
    return 'Invalid date';
  }
};

function ClientOverviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('opportunities');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  // Stats calculations
  const lifetimeValue = opportunities
    .filter(opp => opp.status === 'completed')
    .reduce((sum, opp) => sum + (opp.actual_value || 0), 0);
  const activeProjects = opportunities.filter(opp =>
    !['completed', 'lost'].includes(opp.status)
  ).length;
  const completedJobs = opportunities.filter(opp => opp.status === 'completed').length;

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);

      // Fetch client details
      const clientResponse = await clientsApi.fetchClientById(id);
      setClient(clientResponse.client);

      // Fetch related data in parallel
      const [appointmentsResponse, quotesResponse, invoicesResponse, opportunitiesResponse] = await Promise.allSettled([
        fetchAppointmentsForClient(id),
        quotesApi.fetchQuotes({ clientId: id }),
        invoicesApi.fetchInvoices({ clientId: id }),
        opportunitiesApi.getOpportunitiesByClient(id)
      ]);

      // Handle appointments
      if (appointmentsResponse.status === 'fulfilled') {
        setAppointments(appointmentsResponse.value || []);
      }

      // Handle quotes
      if (quotesResponse.status === 'fulfilled') {
        setQuotes(quotesResponse.value.quotes || []);
      }

      // Handle invoices
      if (invoicesResponse.status === 'fulfilled') {
        setInvoices(invoicesResponse.value.invoices || []);
      }

      // Handle opportunities
      if (opportunitiesResponse.status === 'fulfilled') {
        setOpportunities(opportunitiesResponse.value.opportunities || []);
      }

      setError('');
    } catch (err) {
      console.error('Error fetching client data:', err);
      setError(err.message || 'Failed to load client data.');
    } finally {
      setLoading(false);
    }
  };

  // TODO: Replace with actual appointments API call
  const fetchAppointmentsForClient = async (clientId) => {
    // Placeholder - return empty array for now
    // This should call an actual appointments API endpoint
    return [];
  };

  const handleEditClient = async (updatedClient) => {
    try {
      await clientsApi.updateClient(id, updatedClient);
      setClient({ ...client, ...updatedClient });
      setShowEditModal(false);
    } catch (err) {
      setError(err.message || 'Failed to update client.');
    }
  };

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!client) {
    return <ErrorMessage message="Client not found." />;
  }

  const tabs = [
    { id: 'opportunities', label: 'Opportunities', count: opportunities.length },
    { id: 'appointments', label: 'Appointments', count: appointments.length },
    { id: 'quotes', label: 'Quotes', count: quotes.length },
    { id: 'invoices', label: 'Invoices', count: invoices.length }
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Clients
          </button>
        </div>

        {/* Section 1: Client Contact Info */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          {/* Header with Title and Action Button */}
          <PageHeader title={client.name}>
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
            >
              Edit Client
            </button>
          </PageHeader>

          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
              <div className="flex-1">
                <div className="space-y-2 text-gray-600">
                  {client.email && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.address && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{client.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(lifetimeValue)}</p>
                <p className="text-gray-600">Lifetime Value</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{activeProjects}</p>
                <p className="text-gray-600">Active Projects</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{completedJobs}</p>
                <p className="text-gray-600">Completed Jobs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Tabbed Content */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          {/* Desktop Tabs */}
          <div className="hidden sm:block border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${activeTab === tab.id
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Tabs */}
          <div className="sm:hidden border-b border-gray-200">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full px-6 py-4 text-base font-medium text-gray-900 bg-white border-0 focus:ring-0"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.label} ({tab.count})
                </option>
              ))}
            </select>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'opportunities' && (
              <OpportunitiesTab opportunities={opportunities} clientId={id} />
            )}
            {activeTab === 'appointments' && (
              <AppointmentsTab appointments={appointments} />
            )}
            {activeTab === 'quotes' && (
              <QuotesTab quotes={quotes} />
            )}
            {activeTab === 'invoices' && (
              <InvoicesTab invoices={invoices} />
            )}
          </div>
        </div>

        {/* Section 4: Quick Actions */}
        <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to={`/quotes/new?clientId=${client.id}`}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
            >
              + New Quote
            </Link>
            <button
              onClick={() => setShowAppointmentModal(true)}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 transition duration-300"
            >
              + Schedule Appointment
            </button>
            <button
              onClick={() => alert('Add Note functionality coming soon!')}
              className="px-4 py-2 bg-gray-600 text-white font-medium rounded-lg shadow-md hover:bg-gray-700 transition duration-300"
            >
              + Add Note
            </button>
          </div>
        </div>

        {/* Edit Client Modal */}
        {showEditModal && (
          <EditClientModal
            client={client}
            onClose={() => setShowEditModal(false)}
            onSave={handleEditClient}
          />
        )}

        {/* Create Appointment Modal */}
        {showAppointmentModal && (
          <CreateAppointmentModal
            client={client}
            onClose={() => setShowAppointmentModal(false)}
            onSuccess={() => {
              setShowAppointmentModal(false);
              fetchClientData(); // Refresh data to show new appointment
            }}
          />
        )}
      </div>
    </div>
  );
}

// Opportunities Tab Component
const OpportunitiesTab = ({ opportunities, clientId }) => {
  const getStatusColor = (status) => {
    const statusColors = {
      'lead': 'bg-gray-100 text-gray-800',
      'appointment_requested': 'bg-yellow-100 text-yellow-800',
      'appointment_scheduled': 'bg-blue-100 text-blue-800',
      'quote_draft': 'bg-purple-100 text-purple-800',
      'quote_sent': 'bg-indigo-100 text-indigo-800',
      'quote_accepted': 'bg-green-100 text-green-800',
      'in_progress': 'bg-cyan-100 text-cyan-800',
      'completed': 'bg-green-200 text-green-900',
      'lost': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatusLabel = (status) => {
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (opportunities.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities</h3>
        <p className="text-gray-600">This client doesn't have any opportunities yet.</p>
        <Link
          to={`/pipeline`}
          className="mt-4 inline-block px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700"
        >
          Create New Opportunity
        </Link>
      </div>
    );
  }

  // Sort opportunities: active first, then by date
  const sortedOpportunities = [...opportunities].sort((a, b) => {
    const activeStatuses = ['lead', 'appointment_requested', 'appointment_scheduled', 'quote_draft', 'quote_sent', 'quote_accepted', 'in_progress'];
    const aIsActive = activeStatuses.includes(a.status);
    const bIsActive = activeStatuses.includes(b.status);

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // If both are active or both inactive, sort by date (newest first)
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="space-y-4">
      {sortedOpportunities.map((opportunity) => {
        let actionLink = null;
        let actionLabel = null;
        let actionOnClick = null;

        // Determine dynamic link based on status
        if (['quote_draft', 'quote_sent'].includes(opportunity.status) && opportunity.quote_id) {
          actionLink = `/quotes/edit/${opportunity.quote_id}`;
          actionLabel = 'Edit Quote →';
        } else if (['quote_accepted', 'in_progress'].includes(opportunity.status) && opportunity.quote_id) {
          actionLink = `/quotes/edit/${opportunity.quote_id}`; // Fallback to editor for now
          actionLabel = 'View Quote →';
        } else if (opportunity.status === 'completed') {
          actionLink = `/pipeline`; // Fallback to pipeline
          actionLabel = 'View Details →';
        } else if (['appointment_requested', 'appointment_scheduled'].includes(opportunity.status)) {
          if (opportunity.appointment_id) {
            actionLink = '#';
            actionLabel = 'View Appointment →';
            actionOnClick = (e) => {
              e.preventDefault();
              alert('Please switch to the Appointments tab to view details.');
            };
          }
        }

        return (
          <div key={opportunity.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-medium text-gray-900">{opportunity.project_name || 'Untitled Project'}</h4>
                <div className="flex items-center mt-1 text-sm text-gray-600">
                  <span className="mr-3">Created {formatDate(opportunity.created_at)}</span>
                  {opportunity.estimated_value > 0 && (
                    <span>Est. {formatCurrency(opportunity.estimated_value)}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(opportunity.status)}`}>
                  {formatStatusLabel(opportunity.status)}
                </span>

                {/* Dynamic Action Link */}
                {actionLink && (
                  <Link
                    to={actionLink}
                    onClick={actionOnClick}
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                  >
                    {actionLabel}
                  </Link>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Appointments Tab Component
const AppointmentsTab = ({ appointments }) => {
  if (appointments.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-9 0h10a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments</h3>
        <p className="text-gray-600">This client hasn't had any appointments scheduled yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {appointments.map((appointment) => (
        <div key={appointment.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-medium text-gray-900">{appointment.title}</h4>
              <p className="text-sm text-gray-600 mt-1">
                {formatDate(appointment.appointment_date)} at {appointment.appointment_time || 'Time TBD'}
              </p>
              {appointment.location && (
                <p className="text-sm text-gray-600">📍 {appointment.location}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                {appointment.status}
              </span>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Quotes Tab Component
const QuotesTab = ({ quotes }) => {
  if (quotes.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes</h3>
        <p className="text-gray-600">This client doesn't have any quotes yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quotes.map((quote) => (
        <div key={quote.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <Link
                to={`/quotes/edit/${quote.id}`}
                className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
              >
                {quote.quoteName || `Quote #${quote.id}`}
              </Link>
              <p className="text-sm text-gray-600 mt-1">
                Created {formatDate(quote.createdAt)} • {formatCurrency(quote.grandTotal)}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                quote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                }`}>
                {quote.status}
              </span>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Invoices Tab Component
const InvoicesTab = ({ invoices }) => {
  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices</h3>
        <p className="text-gray-600">This client doesn't have any invoices yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {invoices.map((invoice) => (
        <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <Link
                to={`/invoices/edit/${invoice.id}`}
                className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
              >
                Invoice #{invoice.invoiceNumber || invoice.id}
              </Link>
              <p className="text-sm text-gray-600 mt-1">
                Due {formatDate(invoice.dueDate)} • {formatCurrency(invoice.grandTotal)}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs rounded-full ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                {invoice.status}
              </span>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Edit Client Modal Component
const EditClientModal = ({ client, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Edit Client">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <textarea
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-300"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition duration-300 disabled:opacity-50"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Create Appointment Modal Component
const CreateAppointmentModal = ({ client, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: `Appointment with ${client.name}`,
    description: '',
    startDatetime: '',
    duration: 60, // Duration in minutes (default 1 hour)
    location: client.address || '',
    status: 'scheduled'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // Import appointments API
      const { createAppointment } = await import('../api/appointments');

      // Calculate end time from start time + duration
      const startDate = new Date(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000));

      const appointmentData = {
        clientId: client.id,
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: endDate.toISOString().slice(0, 16), // Format for datetime-local
        location: formData.location,
        status: formData.status
      };

      await createAppointment(appointmentData);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Schedule Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-blue-900 mb-2">Scheduling for:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Client:</strong> {client.name}</p>
            {client.email && <p><strong>Email:</strong> {client.email}</p>}
            {client.phone && <p><strong>Phone:</strong> {client.phone}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Appointment Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.startDatetime}
              onChange={(e) => setFormData({ ...formData, startDatetime: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duration *</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
        </div>

        {/* Show calculated end time */}
        {formData.startDatetime && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-600">
              <strong>Appointment will end at:</strong> {' '}
              {new Date(new Date(formData.startDatetime).getTime() + (formData.duration * 60000)).toLocaleString()}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rescheduled">Rescheduled</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description/Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="3"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Appointment'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ClientOverviewPage;